import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { Store } from "@ngrx/store";
import { getCurrentAccount } from "../accounts/accounts.reducer";
import { Observable, firstValueFrom } from "rxjs";
import {
  OnboardingStep,
  OnboardingProgress,
  VideoTutorial,
} from "./start.interface";
import { MatDialog } from "@angular/material/dialog";
import { VideoDialogComponent } from "../video-dialog/video-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import {
  getVideoTutorialsForLanguage,
  getTutorialVideoUrl,
  getRelatedVideoIds,
} from "./start.data";
import { AuthService } from "../auth/auth.service";

@Component({
  selector: "app-start",
  templateUrl: "./start.component.html",
  styleUrls: ["./start.component.scss"],
  standalone: false,
})
export class StartComponent implements OnInit {
  currentStep = 1;
  totalSteps = 3;
  currentDomain$: Observable<string | undefined>;

  readonly onboardingSteps: OnboardingStep[] = this.createOnboardingSteps();
  videoTutorials: VideoTutorial[] = this.createVideoTutorials();

  private previousStatuses = new Map<string, OnboardingStep["status"]>();

  @ViewChild("livechatWidget", { static: false }) livechatWidget: ElementRef;

  getProviderName(): string {
    const provider = this.auth.appUser?.provider;
    if (provider === "GOOGLE") {
      return "Google";
    } else if (provider === "MICROSOFT") {
      return "Microsoft";
    }
    return "Microsoft / Google";
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private store: Store,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private auth: AuthService,
  ) {
    this.currentDomain$ = this.store.select(getCurrentAccount);

    this.translateService.onLangChange.subscribe(() => {
      this.updateVideoTutorialsForLanguage();
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadOnboardingProgress();
    this.updateVideoTutorialsForLanguage();
  }

  async loadOnboardingProgress(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<OnboardingProgress>("/start/progress"),
      );
      this.updateStepsFromProgress(response || {});
      this.updateVideoProgress(response || {});
    } catch {
      this.updateStepsFromProgress({});
      this.updateVideoProgress({});
    } finally {
      this.updateCurrentStep();
      this.expandCompletedStep();
    }
  }

  async skipStep(stepId: string): Promise<void> {
    const localStep = this.onboardingSteps.find((s) => s.id === stepId);
    if (localStep) {
      if (
        localStep.status === "skipped" ||
        this.previousStatuses.get(stepId) === "skipped"
      ) {
        this.updateStepStatus(stepId, "skipped");
        return;
      }
    }

    const response = await firstValueFrom(
      this.http.get<OnboardingProgress>("/start/progress"),
    );
    const stepMapping = {
      onboarding: "onboarding",
      sync: "sync",
      installation: "installation",
    };
    const progressKey = stepMapping[stepId as keyof typeof stepMapping];
    const isCompleted = response?.[progressKey] === "COMPLETED";

    if (isCompleted) {
      await firstValueFrom(
        this.http.post(`/start/step/${stepId}/complete`, {}),
      );
      this.updateStepStatus(stepId, "completed");
    } else {
      await firstValueFrom(this.http.post(`/start/step/${stepId}/skip`, {}));
      this.updateStepStatus(stepId, "skipped");
    }
  }

  goToStep(stepId: string): void {
    const step = this.onboardingSteps.find((s) => s.id === stepId);
    if (!step?.action?.route) {
      return;
    }

    if (stepId === "sync") {
      sessionStorage.setItem("fromStartPage", "true");
    }

    if (step.status === "skipped") {
      this.activateStep(stepId);
      return;
    }

    if (step.status === "completed") {
      if (stepId === "installation" && step.selectedSignatureId) {
        void this.router.navigate([
          "/signatures/custom",
          step.selectedSignatureId,
        ]);
      } else {
        void this.router.navigate([step.action.route]);
      }
      return;
    }

    if (stepId !== "onboarding") {
      this.updateStepStatus(stepId, "completed");
    }

    if (stepId === "onboarding") {
      void this.navigateToOnboarding();
    } else if (stepId === "installation" && step.selectedSignatureId) {
      void this.router.navigate([
        "/signatures/custom",
        step.selectedSignatureId,
      ]);
    } else {
      void this.router
        .navigate([step.action.route])
        .then(() => this.scheduleStatusCheck(stepId));
    }
  }

  goToCreateNewSignature(): void {
    const appUser = this.auth.appUser;
    this.auth.getGeneratorToken(appUser).subscribe((token) => {
      const url = `https://generator.gsignature.com/?token=${token}`;
      window.open(url, "_blank");
    });
  }

  goToChooseTemplate(): void {
    void this.router.navigate(["/signatures/custom"]);
  }

  activateStep(stepId: string): void {
    const step = this.onboardingSteps.find((s) => s.id === stepId);
    if (!step) {
      return;
    }

    const currentActiveStep = this.onboardingSteps.find(
      (s) => s.status === "active",
    );
    if (currentActiveStep && currentActiveStep.id !== stepId) {
      const prev = this.previousStatuses.get(currentActiveStep.id);
      if (prev === "completed" || prev === "skipped") {
        currentActiveStep.status = prev;
      } else {
        currentActiveStep.status = "";
      }
      this.previousStatuses.delete(currentActiveStep.id);
    }

    if (
      !this.previousStatuses.has(step.id) &&
      (step.status === "completed" || step.status === "skipped")
    ) {
      this.previousStatuses.set(step.id, step.status);
    }

    step.status = "active";
    step.isExpanded = true;

    this.updateCurrentStep();
    this.expandCompletedStep();
  }

  openVideoModal(video: VideoTutorial): void {
    const dialogRef = this.dialog.open(VideoDialogComponent, {
      width: "800px",
      panelClass: "app-dialog-panel",
      data: { video },
    });

    dialogRef.afterClosed().subscribe(() => {
      if (video && !video.watched) {
        video.watched = true;
        void this.saveVideoProgress();
      }
    });
  }

  goToYoutubeChannel(): void {
    window.open(
      "https://www.youtube.com/@gSignature_email_signatures",
      "_blank",
    );
  }

  private updateVideoProgress(progress: Partial<OnboardingProgress>): void {
    const watchedVideos = progress?.watchedVideos || [];

    this.videoTutorials.forEach((video) => {
      const isDirectlyWatched = watchedVideos.includes(video.id);
      const relatedIds = getRelatedVideoIds(video.id);
      const isRelatedWatched = relatedIds.some((relatedId) =>
        watchedVideos.includes(relatedId),
      );

      video.watched = isDirectlyWatched || isRelatedWatched;
    });
  }

  private async saveVideoProgress(): Promise<void> {
    const watchedVideos = this.videoTutorials
      .filter((video) => video.watched)
      .map((video) => video.id);

    const allRelatedIds = new Set<string>();
    watchedVideos.forEach((videoId) => {
      allRelatedIds.add(videoId);
      const relatedIds = getRelatedVideoIds(videoId);
      relatedIds.forEach((relatedId) => allRelatedIds.add(relatedId));
    });

    await firstValueFrom(
      this.http.post("/start/video-progress", {
        watchedVideos: Array.from(allRelatedIds),
      }),
    );
  }

  private createOnboardingSteps(): OnboardingStep[] {
    const steps = [
      { id: "onboarding", title: "onboarding", route: "/onboarding" },
      { id: "sync", title: "sync", route: "/settings/integration" },
      { id: "installation", title: "installation", route: "/signatures" },
    ];

    return steps.map((step) => ({
      id: step.id,
      title: step.title,
      status: "",
      isExpanded: false,
      menuItems: [{ icon: this.getStepIcon(step.id), text: "" }],
      action: { text: "", route: step.route },
      skipAction: { text: "", action: () => this.skipStep(step.id) },
    }));
  }

  private createVideoTutorials(): VideoTutorial[] {
    const currentLang = this.translateService.currentLang || "en";

    return getVideoTutorialsForLanguage(currentLang);
  }

  private getStepIcon(stepId: string): string {
    const icons = {
      onboarding: "dashboard",
      sync: "sync",
      installation: "email",
    };
    return icons[stepId as keyof typeof icons] || "help";
  }

  private updateStepsFromProgress(progress: Partial<OnboardingProgress>): void {
    const stepMapping = {
      onboarding: "onboarding",
      sync: "sync",
      installation: "installation",
    };

    this.previousStatuses.clear();

    this.onboardingSteps.forEach((step) => {
      const progressKey = stepMapping[
        step.id as keyof typeof stepMapping
      ] as keyof OnboardingProgress;
      const progressValue = progress[progressKey];
      step.status =
        progressValue === "COMPLETED"
          ? "completed"
          : progressValue === "SKIPPED"
            ? "skipped"
            : "";
    });

    const firstEmptyIndex = this.onboardingSteps.findIndex(
      (step) => step.status === "",
    );
    if (firstEmptyIndex !== -1) {
      this.onboardingSteps[firstEmptyIndex].status = "active";
      this.currentStep = firstEmptyIndex + 1;
    }
  }

  private updateCurrentStep(): void {
    const completedSteps = this.onboardingSteps.filter(
      (step) => step.status === "completed",
    ).length;
    const skippedSteps = this.onboardingSteps.filter(
      (step) => step.status === "skipped",
    ).length;

    if (completedSteps + skippedSteps === this.totalSteps) {
      this.currentStep = this.totalSteps;
    } else {
      const activeStepIndex = this.onboardingSteps.findIndex(
        (step) => step.status === "active",
      );
      if (activeStepIndex !== -1) {
        this.currentStep = completedSteps + skippedSteps + 1;
      }
    }
  }

  private expandCompletedStep(): void {
    this.onboardingSteps.forEach((step) => {
      step.isExpanded = step.status === "active";
    });
  }

  private updateStepStatus(
    stepId: string,
    status: "completed" | "skipped",
  ): void {
    const step = this.onboardingSteps.find((s) => s.id === stepId);
    if (step) {
      this.previousStatuses.delete(stepId);
      step.status = status;
      this.activateNextStep(stepId);
      this.updateCurrentStep();
      this.expandCompletedStep();
    }
  }

  private activateNextStep(currentStepId: string): void {
    const currentIndex = this.onboardingSteps.findIndex(
      (s) => s.id === currentStepId,
    );
    const nextStep = this.onboardingSteps[currentIndex + 1];

    if (nextStep && nextStep.status === "") {
      nextStep.status = "active";
    }
  }

  private async navigateToOnboarding(): Promise<void> {
    const domain = await firstValueFrom(this.currentDomain$);
    const queryParams = domain ? { domain, fromStart: "true" } : {};

    void this.router.navigate(["/onboarding"], { queryParams }).then(() => {
      this.scheduleStatusCheck("onboarding");
    });
  }

  private scheduleStatusCheck(stepId: string): void {
    setTimeout(() => this.checkStepCompletion(stepId), 5000);
  }

  private async checkStepCompletion(stepId: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.get<OnboardingProgress>("/start/progress"),
    );
    const stepKey = stepId as keyof OnboardingProgress;
    const isCompleted = response?.[stepKey] === "COMPLETED";

    if (isCompleted) {
      await this.markStepCompleted(stepId);
    }
  }

  private async markStepCompleted(stepId: string): Promise<void> {
    await firstValueFrom(this.http.post(`/start/step/${stepId}/complete`, {}));
    this.updateStepStatus(stepId, "completed");
  }

  private updateVideoTutorialsForLanguage(): void {
    const watchedVideoIds = this.videoTutorials
      .filter((video) => video.watched)
      .map((video) => video.id);

    this.videoTutorials = this.createVideoTutorials();

    this.videoTutorials.forEach((video) => {
      const isDirectlyWatched = watchedVideoIds.includes(video.id);
      const relatedIds = getRelatedVideoIds(video.id);
      const isRelatedWatched = relatedIds.some((relatedId) =>
        watchedVideoIds.includes(relatedId),
      );

      video.watched = isDirectlyWatched || isRelatedWatched;
    });
  }

  openLiveChat(): void {
    if (window.LiveChatWidget) {
      window.LiveChatWidget.call("maximize");
    }
  }

  openTutorialVideo(): void {
    const currentLang = this.translateService.currentLang || "en";
    const tutorialVideoUrl = getTutorialVideoUrl(currentLang);

    window.open(tutorialVideoUrl, "_blank");
  }

  openHelpCenter(): void {
    window.open("https://gsignature.com/support", "_blank");
  }
}
